import React, { Component, ReactNode } from "react";
import { RotateCcw, AlertCircle, FileWarning } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "../../../ui/alert";
import { Button } from "../../../ui/button";

interface Props {
  children: ReactNode;
  onReset?: () => void;
  documentType?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component specifically for the ID validation flow.
 *
 * Catches JavaScript errors in the ID validation components and displays
 * a user-friendly fallback UI. This includes errors from:
 * - IDValidationLoader
 * - AddressConfirmationForm
 * - IDDataConfirmationForm
 * - IDDataEditForm
 * - useIDValidationFlow hook
 *
 * The boundary provides a reset mechanism to allow users to retry the validation.
 */
export class IDValidationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error for debugging in development only
    if (import.meta.env.DEV) {
      console.error("[IDValidationErrorBoundary] Error:", error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { documentType } = this.props;
      const documentLabel = documentType ? ` (${documentType.toUpperCase()})` : "";

      return (
        <div className="p-6 min-h-[300px] flex items-center justify-center">
          <div className="max-w-md w-full">
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-5 w-5" />
              <AlertTitle className="text-lg">
                Error en la verificacion de documento{documentLabel}
              </AlertTitle>
              <AlertDescription className="mt-2">
                <p className="mb-3">
                  Ocurrio un error inesperado durante la verificacion del documento.
                  Por favor, intenta de nuevo.
                </p>
                {import.meta.env.DEV && this.state.error && (
                  <p className="text-xs text-muted-foreground font-mono mb-3 p-2 bg-red-50 rounded border border-red-200">
                    {this.state.error.message}
                  </p>
                )}
              </AlertDescription>
            </Alert>

            <div className="flex flex-col gap-3">
              <Button
                variant="default"
                className="w-full"
                onClick={this.handleReset}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reintentar verificacion
              </Button>

              <div className="text-center text-sm text-gray-500">
                <FileWarning className="h-4 w-4 inline mr-1" />
                Si el problema persiste, intenta con una imagen diferente.
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default IDValidationErrorBoundary;
